// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define terms.
 *
 * The audience knows most of these, but "training example limit" and "billable
 * tokens" are exactly the two the tool is arguing about, so they need to be
 * defined where they are used rather than in a modal somebody has to find.
 */

export const GLOSSARY: Readonly<Record<string, string>> = {
  token:
    'The unit a model actually reads. Not a word and not a character — roughly three to four characters of English, but far fewer for code, and often one per character for Chinese, Japanese or Korean. Everything a provider charges for is counted in these.',
  tokenizer:
    'The program that turns text into tokens. Each model family has its own, and they disagree: the same sentence is a different number of tokens for GPT-4.1 and for Qwen. epochcost runs the real one rather than estimating from character counts.',
  'training example limit':
    'The most tokens one record in your training file may contain. It is NOT the model’s context window and is often much smaller — for the gpt-4.1 and gpt-4o families it is 65,536 tokens against a 128,000-token window. A record longer than this is cut down before training.',
  'context window':
    'The most tokens a model can read in one request when you use it. Checking a training file against this number rather than the training example limit is the common mistake, because it passes records that will still be cut.',
  truncation:
    'Cutting a record down to the limit. Tokens are removed from the END, and in a chat record the end is the assistant’s reply — so the model is shown the question with part of the answer missing, which is a lesson in stopping early.',
  'billable tokens':
    'What you are charged for: the sum over records of the lesser of the record’s length and the limit, multiplied by the number of epochs. Records longer than the limit are billed at the limit, because that is all the trainer keeps.',
  epoch:
    'One complete pass over your training file. Providers bill per token per epoch, so three epochs over a million tokens costs three million tokens.',
  jsonl:
    'JSON Lines: one complete JSON object per line, with no commas or brackets around the whole file. The standard format for training data, because a reader can stream it without holding the file in memory.',
  'chat framing':
    'The tokens a chat format adds around each message — the role and the separators — which the model reads but which are not in your "content" string. Counting content alone undercounts; counting the raw JSON line overcounts, because the model never sees the quotes or the word "content".',
  lora:
    'Low-Rank Adaptation: fine-tuning by training a small number of extra weights rather than all of them. Much cheaper per token, which is why providers price it separately.',
};

let tooltip: HTMLElement | null = null;

function hide(): void {
  tooltip?.remove();
  tooltip = null;
}

function show(anchor: HTMLElement, term: string): void {
  hide();
  const definition = GLOSSARY[term];
  if (!definition) return;

  const node = document.createElement('div');
  node.className = 'glossary-tip';
  node.setAttribute('role', 'tooltip');

  const heading = document.createElement('strong');
  heading.textContent = term;
  const body = document.createElement('p');
  body.textContent = definition;
  node.append(heading, body);
  document.body.appendChild(node);

  const box = anchor.getBoundingClientRect();
  const width = node.offsetWidth;
  const left = Math.min(Math.max(8, box.left), window.innerWidth - width - 8);
  const above = box.top > node.offsetHeight + 16;
  node.style.left = `${left}px`;
  node.style.top = above ? `${box.top - node.offsetHeight - 8}px` : `${box.bottom + 8}px`;
  tooltip = node;
}

/**
 * Wire the whole document once.
 *
 * Delegated from the document rather than bound per span, because the interface
 * re-renders and re-binding on every render leaks listeners.
 */
export function mountGlossary(): void {
  document.addEventListener('click', (ev) => {
    const target = (ev.target as HTMLElement | null)?.closest?.('.glossary-link') as HTMLElement | null;
    if (target) {
      const term = target.dataset.term;
      if (term) {
        if (tooltip && tooltip.dataset.term === term) hide();
        else {
          show(target, term);
          if (tooltip) tooltip.dataset.term = term;
        }
        ev.stopPropagation();
        return;
      }
    }
    hide();
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') hide();
  });

  window.addEventListener('resize', hide, { passive: true });
  window.addEventListener('scroll', hide, { passive: true, capture: true });
}

/**
 * A term the reader can click.
 *
 * A `<button>` rather than a styled `<span>`: a span with a click handler is
 * invisible to the keyboard and to a screen reader, and a sibling tool shipped
 * exactly that and had to fix it.
 */
export function term(text: string, key = text): HTMLButtonElement {
  const node = document.createElement('button');
  node.type = 'button';
  node.className = 'glossary-link';
  node.dataset.term = key;
  node.textContent = text;
  node.setAttribute('aria-label', `${text} — what this means`);
  return node;
}
